import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from '@tanstack/react-router';
import { ThemeProvider } from 'next-themes';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { queryClient, router } from '@/app/router';
import type { AppCommonUsersRouterMeResponse } from '@/shared/api/generated/api.schemas';
import { useGetMeApiUsersMeGet } from '@/shared/api/generated/users/users';
import { config } from '@/shared/config/env';
import { logger } from '@/shared/lib/logger';
import { initMsw } from '@/shared/lib/msw';
import { setNavigationListener } from '@/shared/lib/navigation';
import { type User, useAuthStore } from '@/shared/store/auth';
import { OAuthDialog } from '@/shared/ui/oauth-dialog';
import { Toaster } from '@/shared/ui/sonner';

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <h1 className="text-2xl font-bold">Something went wrong</h1>
      <pre className="mt-4 text-sm text-red-500">{error.message}</pre>
    </div>
  );
}

/**
 * MSW 실패 경고 배너
 */
function MswWarningBanner() {
  return (
    <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-yellow-900 px-4 py-2 text-center text-sm font-medium z-50">
      ⚠️ MSW 초기화 실패: API 요청이 실제 서버로 전송됩니다. 개발 환경을 확인하세요.
    </div>
  );
}

/**
 * 로딩 스피너 컴포넌트
 */
function LoadingSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

/**
 * 세션 체크 및 라우터 렌더링 컴포넌트
 *
 * QueryClientProvider 내부에서 실행되어야 하므로 별도 컴포넌트로 분리
 */
function SessionCheckerAndRouter({ mswFailed }: { mswFailed: boolean }) {
  const { setUser, clearUser, setSessionChecked } = useAuthStore();
  const isSessionChecked = useAuthStore((state) => state.isSessionChecked);

  // bfcache (back-forward cache) 처리
  // 브라우저 뒤로가기로 페이지가 복원될 때 stale 상태 방지
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // bfcache에서 복원된 경우 모든 쿼리 무효화
        queryClient.invalidateQueries();
        logger.debug('Page restored from bfcache, invalidating queries');
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // 세션 체크 (앱 시작 시 GET /api/users/me 호출)
  const { data, isLoading: isSessionLoading } = useGetMeApiUsersMeGet({
    query: {
      retry: false,
      staleTime: 5 * 60 * 1000, // 5분 캐시
    },
  });

  // 세션 체크 결과 처리
  useEffect(() => {
    if (isSessionLoading) return;

    const meResponse = data as AppCommonUsersRouterMeResponse | undefined;

    if (meResponse?.authenticated && meResponse.user) {
      // 세션 유효: 사용자 정보 저장
      // AppCommonUsersRouterUserResponse를 User 타입으로 변환
      const user: User = {
        id: meResponse.user.id,
        email: meResponse.user.email,
        name: meResponse.user.name,
        role: meResponse.user.role,
      };
      setUser(user);
      logger.info('Session restored', {
        action: 'auth_session',
        metadata: { userId: meResponse.user.id },
      });
    } else {
      // 세션 무효: 로그아웃 상태로 초기화
      clearUser();
      logger.debug('No active session', { action: 'auth_session' });
    }

    setSessionChecked(true);
  }, [data, isSessionLoading, setUser, clearUser, setSessionChecked]);

  // 네비게이션 리스너 등록 (순환 참조 방지)
  useEffect(() => {
    setNavigationListener(
      (path) => {
        router.navigate({ to: path });
      },
      () => router.state.location.pathname,
    );

    return () => {
      setNavigationListener(null, null);
    };
  }, []);

  // 세션 체크 완료될 때까지 대기
  if (!isSessionChecked) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {mswFailed && <MswWarningBanner />}
      <RouterProvider router={router} />
      <Toaster />
      <OAuthDialog />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </>
  );
}

export function AppProvider() {
  const [isMswReady, setIsMswReady] = useState(!config.enableMsw);
  const [mswFailed, setMswFailed] = useState(false);

  // MSW 활성화 (개발 환경 또는 VITE_ENABLE_MSW=true)
  useEffect(() => {
    async function initMocks() {
      if (config.enableMsw) {
        const { success } = await initMsw();
        if (!success) {
          setMswFailed(true);
        }
      }

      setIsMswReady(true);
    }

    initMocks();
  }, []);

  // MSW 준비될 때까지 대기
  if (!isMswReady) {
    return <LoadingSpinner />;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        disableTransitionOnChange
      >
        <QueryClientProvider client={queryClient}>
          <SessionCheckerAndRouter mswFailed={mswFailed} />
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
